import React from 'react'

import { CarouselBanner } from './CarouselBanner'
import { CarouselListItem } from './CarouselListItem'
import { CitrusRenderer } from './Citrus'
import { ListItemSDUIRenderer } from './ListItemSDUIRenderer'
import { ListItemGeneralSDUIRenderer } from './ListItemGeneralSDUIRenderer'
import { ListItemActionSDUIRenderer } from './ListItemActionSDUIRenderer'
import { ListItemControlSDUIRenderer } from './ListItemControlSDUIRenderer'
import { Header } from './Header'
import { ButtonComponent } from './Button'
import { TextComponent } from './Text'
import { InputComponent } from './Input'
import { AccordionComponent } from './Accordion'
import { CarouselMenuItem } from './CarouselMenuItem'
import { SectionHeaderSDUI } from './SectionHeaderSDUI'
import type { ComponentData, ComponentType, SDUIComponentProps } from './interfaces'

// Component renderers
const renderHeader = (props: ComponentData) => (
  <Header
    titleHeader={props.titleHeader}
    descriptionHeader={props.descriptionHeader}
    titleStyle={props.titleStyle}
    textSize={props.textSize}
  />
)

const renderButton = (props: ComponentData) => (
  <ButtonComponent text={props.text} onClick={props.onClick} hierarchy={props.hierarchy} />
)

const renderText = (props: ComponentData) => {
  const sduiProps = props.sduiComponent?.props as Record<string, unknown> | undefined
  return (
    <TextComponent
      text={(sduiProps?.text as string) ?? props.text}
      textSize={(sduiProps?.textSize as ComponentData['textSize']) ?? props.textSize}
      textType={(sduiProps?.textType as ComponentData['textType']) ?? props.textType}
      textWeight={(sduiProps?.textWeight as ComponentData['textWeight']) ?? props.textWeight}
      typeElement={(sduiProps?.typeElement as ComponentData['typeElement']) ?? props.typeElement}
    />
  )
}

const renderInput = (props: ComponentData) => {
  const sduiProps = props.sduiComponent?.props as Record<string, unknown> | undefined
  return (
    <InputComponent
      label={(sduiProps?.label as string) ?? ''}
      placeholder={(sduiProps?.placeholder as string) ?? undefined}
      type={(sduiProps?.type as 'text' | 'number') ?? 'text'}
      mask={(sduiProps?.mask as 'email' | 'phone' | 'cep' | 'cpf' | 'date') ?? undefined}
    />
  )
}

const renderAccordion = (props: ComponentData) => <AccordionComponent items={props.items} />

const renderCarouselMenuItem = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  return <CarouselMenuItem component={props.sduiComponent} />
}

const renderListItemSDUI = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  return <ListItemSDUIRenderer sduiComponent={props.sduiComponent} />
}

const renderListItemGeneralSDUI = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  return <ListItemGeneralSDUIRenderer sduiComponent={props.sduiComponent} />
}

const renderListItemActionSDUI = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  return <ListItemActionSDUIRenderer sduiComponent={props.sduiComponent} />
}

const renderListItemControlSDUI = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  return <ListItemControlSDUIRenderer sduiComponent={props.sduiComponent} />
}

const renderCarouselListItem = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  const { sduiComponent } = props
  const items = (sduiComponent?.props?.items ?? []) as CarouselListItemData[]

  return (
    <CarouselListItem
      items={items}
      style={{
        marginTop: sduiComponent.style?.marginTop,
        marginBottom: sduiComponent.style?.marginBottom,
      }}
    />
  )
}

const renderCitrus = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  return <CitrusRenderer component={props.sduiComponent} />
}

const renderCarouselBanner = (props: ComponentData) => {
  if (!props.sduiComponent) return <></>
  const { sduiComponent } = props
  const items = (sduiComponent?.props?.items ?? []) as BannerComponent[]

  return <CarouselBanner items={items} style={sduiComponent.style} />
}

const renderSectionHeaderSDUI = (props: ComponentData) => (
  <SectionHeaderSDUI sduiComponent={props.sduiComponent} />
)

const componentRenderers: Partial<Record<ComponentType, (props: ComponentData) => JSX.Element>> = {
  header: renderHeader,
  button: renderButton,
  text: renderText,
  input: renderInput,
  accordion: renderAccordion,
  'carousel-menu-item': renderCarouselMenuItem,
  'list-item': renderListItemSDUI,
  'list-item-general': renderListItemGeneralSDUI,
  'list-item-action': renderListItemActionSDUI,
  'list-item-control': renderListItemControlSDUI,
  'carousel-list-item': renderCarouselListItem,
  citrus: renderCitrus,
  'carousel-banner': renderCarouselBanner,
  'section-header': renderSectionHeaderSDUI,
}

const DynamicComponent: React.FC<ComponentData> = (props) => {
  const { type, styles, text } = props
  const renderer = componentRenderers[type]

  if (renderer) {
    return renderer(props)
  }

  return <p style={styles}>{text}</p>
}

export { ComponentType }
export type { SDUIComponentProps }
export default DynamicComponent
